import React from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import apiClient from '../../shared/services/apiService.js';
import './ModernJobsGrid.css';

function CompanyHeader({ company, companyName }) {
  if (!company) return null;
  const logo = (company.logo_url && (company.logo_url.small || company.logo_url.full)) || company.logo || '';
  const name = company.name || companyName || 'Entreprise';
  const desc = company.description || '';
  const flag = company.country_flag_url;
  return (
    <div className="company-hero card border-0 shadow-sm mb-3 p-3 p-md-4" style={{ borderRadius: 16 }}>
      <div className="d-flex align-items-center gap-3">
        <div style={{ width: 64, height: 64, borderRadius: 12, background: '#f3f4f6' }} className="d-flex align-items-center justify-content-center overflow-hidden">
          {logo ? (
            <img src={logo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <i className="fa-regular fa-building text-muted fs-3"></i>
          )}
        </div>
        <div>
          <h1 className="h5 mb-1 d-flex align-items-center gap-2">
            {flag ? (
              <img src={flag} alt="Drapeau" style={{ width: 20, height: 14, objectFit: 'cover' }} />
            ) : null}
            <span>{name}</span>
          </h1>
          {desc ? <div className="text-muted small">{desc}</div> : null}
        </div>
      </div>
    </div>
  );
}

function PostCard({ item }) {
  const title = item.title || item.name || '—';
  const org = item.company_name || item.company || item.organisation || '';
  const location = (typeof item.city === 'object' ? (item.city?.name || item.city?.label) : (item.city || item.location)) || '—';
  const date = item.created_at_formatted || item.created_at || item.date || '';
  const id = item.id || item.uuid || item.slug;
  const category = (typeof item.category === 'object' ? (item.category?.name) : (item.category_name || item.category)) || '';
  const tags = Array.isArray(item.tags) ? item.tags : [];
  const visits = item.visits_formatted || (typeof item.visits === 'number' ? `${item.visits} vues` : '');
  return (
    <Link to={`/job-detail?id=${id}`} className="text-reset text-decoration-none">
      <div className="modern-job-card hover-raise">
        <div className="job-card-header">
          <div className="job-main-info">
            <div className="job-title-section">
              <h3 className="job-title">{title}</h3>
              <div className="job-company">
                <i className="fa-regular fa-building"></i>
                <span>{org}</span>
              </div>
            </div>
            <div className="job-badges">
              {category ? <span className="job-badge category-badge">{category}</span> : null}
            </div>
          </div>
        </div>
        <div className="job-meta">
          <div className="meta-item">
            <i className="fa-solid fa-location-dot"></i>
            <span>{location}</span>
          </div>
          <div className="meta-item">
            <i className="fa-regular fa-calendar"></i>
            <span>{date}</span>
          </div>
          {visits && (
            <div className="meta-item">
              <i className="fa-regular fa-eye"></i>
              <span>{visits}</span>
            </div>
          )}
        </div>
        {!!tags.length && (
          <div className="job-tags">
            {tags.slice(0, 4).map((t, i) => (
              <span key={i} className="job-tag">#{String(t).toLowerCase().replace(/\s+/g,'')}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function CompanyPosts() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [company, setCompany] = React.useState(null);
  const [posts, setPosts] = React.useState([]);
  const [companyName, setCompanyName] = React.useState('');
  const [meta, setMeta] = React.useState({ total: 0, current_page: 1, last_page: 1, per_page: 20 });
  const page = Number(sp.get('page') || 1);
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState('recent'); // recent|oldest|title

  const COUNTRY_NAMES = React.useMemo(() => ({
    'SN': 'Sénégal',
    'FR': 'France',
    'US': 'États-Unis',
    'GB': 'Royaume-Uni',
    'ES': 'Espagne',
    'DE': 'Allemagne',
    'MA': 'Maroc',
    'CI': "Côte d’Ivoire",
  }), []);
  const countryName = React.useMemo(() => {
    const c = company?.country_code || company?.user?.country_code;
    return (c && COUNTRY_NAMES[c]) || COUNTRY_NAMES['SN'] || '';
  }, [company, COUNTRY_NAMES]);
  const countryFlag = company?.country_flag_url || company?.user?.country_flag_url;

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiClient.get(`/companies/${id}/posts`, {
          headers: { 'X-Skip-Auth': true },
          params: { page },
        });
        const data = res?.data;
        // Flexible shapes: {result:{data:[...], meta:{...}}, extra:{company:{...}}}
        const root = data?.result || data || {};
        const extraCompany = data?.extra?.company || root?.extra?.company;
        const companyInfo = root.company || root.data?.company || root.meta?.company || extraCompany || null;
        let list = root.data || root.posts || [];
        if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data; // paginator shape
        if (!mounted) return;
        setCompany(companyInfo);
        setPosts(Array.isArray(list) ? list : []);
        const m = root.meta || {};
        setMeta({
          total: Number(m.total) || (Array.isArray(list) ? list.length : 0),
          current_page: Number(m.current_page) || page,
          last_page: Number(m.last_page) || 1,
          per_page: Number(m.per_page) || 20,
        });
        // Derive company name if not present in company object
        const derivedName = (companyInfo && companyInfo.name)
          || (Array.isArray(list) && list.length ? (list[0].company_name || list[0].company?.name || list[0].company) : '')
          || '';
        setCompanyName(derivedName);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
        if (mounted) setError(`Impossible de charger les offres de l'entreprise: ${msg}`);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, page]);

  function goTo(p) {
    if (p < 1 || p > meta.last_page || p === page) return;
    const next = new URLSearchParams(sp);
    next.set('page', String(p));
    setSp(next, { replace: true });
  }

  return (
    <div className="page-content modern-jobs-page">
      <section className="content-inner bg-light position-relative overflow-hidden">
        <div className="container">
          <button className="btn btn-link text-success px-0 mb-2" onClick={() => navigate(-1)}>
            <i className="fa-solid fa-arrow-left-long me-1"></i> Retour
          </button>
          {/* Breadcrumb: Pays / Société */}
          <div className="d-flex align-items-center gap-2 text-muted small mb-2" aria-label="Fil d'Ariane">
            <Link to="/" className="text-success me-1">
              <i className="fa-solid fa-house"></i>
            </Link>
            <span className="mx-1">/</span>
            <span className="d-inline-flex align-items-center gap-1 text-success fw-semibold">
              {countryFlag ? (<img src={countryFlag} alt="Drapeau" style={{ width: 20, height: 14, objectFit: 'cover' }} />) : null}
              <span>{countryName}</span>
            </span>
            <span className="mx-1">/</span>
            <span className="fw-semibold" style={{ color: '#374151' }}>{company?.name || companyName || '—'}</span>
          </div>
          <CompanyHeader company={company} companyName={companyName} />

          {/* Summary */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="text-muted small">
              {meta.total} offre{meta.total > 1 ? 's' : ''} trouvée{meta.total > 1 ? 's' : ''} chez <span className="fw-semibold">{company?.name || companyName || '—'}</span>
            </div>
            {typeof company?.posts_count === 'number' && (
              <span className="badge bg-success-subtle text-success border border-success" style={{ fontWeight: 600 }}>
                {company.posts_count} au total
              </span>
            )}
          </div>

          {/* Toolbar: search + sort */}
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <div className="flex-grow-1">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="fa-solid fa-magnifying-glass"></i></span>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Rechercher dans les offres de cette entreprise..."
                  value={query}
                  onChange={(e)=>setQuery(e.target.value)}
                />
              </div>
            </div>
            <div>
              <select className="form-select" value={sort} onChange={(e)=>setSort(e.target.value)}>
                <option value="recent">Plus récentes</option>
                <option value="oldest">Plus anciennes</option>
                <option value="title">Par titre (A→Z)</option>
              </select>
            </div>
          </div>

          <div className="modern-jobs-grid">
            {loading && (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="modern-job-card placeholder-glow">
                    <div className="job-card-header">
                      <div className="job-main-info w-100">
                        <div className="job-title-section">
                          <div className="placeholder col-8 mb-2" style={{ height: 24 }}></div>
                          <div className="placeholder col-4" style={{ height: 16 }}></div>
                        </div>
                      </div>
                    </div>
                    <div className="job-meta">
                      <div className="placeholder col-3" style={{ height: 14 }}></div>
                    </div>
                  </div>
                ))}
              </>
            )}
            {error && !loading && <div className="alert alert-danger">{error}</div>}
            {!loading && !error && posts.length === 0 && (
              <div className="text-muted">Aucune offre trouvée pour cette entreprise.</div>
            )}
            {!loading && !error && (
              (()=>{
                let list = posts.slice();
                const q = query.trim().toLowerCase();
                if (q) {
                  list = list.filter(p => (
                    String(p.title||'').toLowerCase().includes(q)
                    || String(p.company_name||'').toLowerCase().includes(q)
                    || String(p.excerpt||'').toLowerCase().includes(q)
                  ));
                }
                if (sort === 'recent') {
                  list.sort((a,b)=> new Date(b.created_at||b.created_at_formatted||0) - new Date(a.created_at||a.created_at_formatted||0));
                } else if (sort === 'oldest') {
                  list.sort((a,b)=> new Date(a.created_at||a.created_at_formatted||0) - new Date(b.created_at||b.created_at_formatted||0));
                } else if (sort === 'title') {
                  list.sort((a,b)=> String(a.title||'').localeCompare(String(b.title||'')));
                }
                return list.map((p)=> (
                  <PostCard key={p.id || Math.random()} item={p} />
                ));
              })()
            )}
          </div>

          {/* Pagination */}
          {!loading && meta.last_page > 1 && (
            <nav className="d-flex justify-content-center mt-3" aria-label="Pagination des offres de l'entreprise">
              <ul className="pagination">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(1)}>««</button>
                </li>
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(page - 1)}>«</button>
                </li>
                <li className="page-item active"><span className="page-link">{page}</span></li>
                <li className={`page-item ${page >= meta.last_page ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(page + 1)}>»</button>
                </li>
                <li className={`page-item ${page >= meta.last_page ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(meta.last_page)}>»»</button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </div>
  );
}
